import React, {FC, memo, ReactNode} from 'react';
import styled, {css, DefaultTheme} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ChannelTypesEnum} from '../../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {TicketStatusModel} from '../../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {
  buildContactDisplayHandle,
  buildContactDisplayName,
} from '../../../../../helpers/models/contactHelpers';
import {ApplicationChannelTypeModel} from '../../../../../models/api/applicationChannelTypeModel';
import {HighlightModel} from '../../../../../models/api/conversationHighlightModel';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {ExternalChannelTypeModel} from '../../../../../models/api/externalChannelTypeModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {MutationProofText} from '../../../../common/text/mutationProofText';
import {useMaximumNumberOfCellIndicators} from '../../cellContent/cellTagsCommon';
import {Highlight} from '../../header/search/highlight/highlight';
import {CellBadgeValue} from './cellBadgeValue';
import {CellOriginalLinkedIcon} from './cellOriginalLinkedIcon';
import {CellRating} from './cellRating';
import {CellTags} from './cellTags';
import {CellTicketStatusPill} from './cellTicketStatusPill';

/*
 * Props.
 */

interface CellMiddleProps {
  messageType: MessageTypesEnum;
  subject: string;
  subjectHighlight?: HighlightModel;
  recipient?: VisualRecipientModel;
  tags: ReadonlyArray<TagModel>;
  badgeValue: number;
  isOriginalConversation?: boolean;
  isSelected?: boolean;
  isStarred?: boolean;
  externalChannelType?: ExternalChannelTypeModel;
  applicationChannelType?: ApplicationChannelTypeModel;
  numLinkedConversations?: number;
  conversation?: ConversationBaseModel;
  ticketStatus?: TicketStatusModel;
  channelTypes: ReadonlyArray<ChannelTypesEnum>;
}

/*
 * Style.
 */

const StyledFlexDiv = styled.div`
  grid-area: middle-line;
  display: flex;
  min-width: 0;
`;

const StyledGridDiv = styled.div`
  flex: 1;

  display: grid;
  grid-template-columns: auto;
  grid-auto-columns: minmax(auto, max-content);
  grid-auto-flow: column;
  gap: 4px;
`;

interface CellMiddleStyleProps {
  $isSelected?: boolean;
  $hasHighlight?: boolean;
  $isCompact?: boolean;
}

function addCommonStyles(p: CellMiddleStyleProps & {theme: DefaultTheme}) {
  return css`
    /** If we have a highlight, rely on <Highlight /> to use the correct colors. */
    color: ${p.$isSelected && !p.$hasHighlight ? p.theme.invertedText : p.theme.greys.shade90};

    /* 
      This padding-margin trick lets the subject highlight background overflow
      on the left, with its rounded corners, instead of starting abruptly.
    */
    padding-left: 3px;
    /* 
      In the compact case, because this element is in a flexbox, we have to move
      the margin-left property to the first parent that is not inside a flexbox.

      See StyledCellBaseMiddleThird in compactCellContentCommon.tsx.
    */
    ${!p.$isCompact && 'margin-left: -3px;'}

    ${ellipsis()};
  `;
}

const StyledSubjectDiv = styled.div<CellMiddleStyleProps>`
  ${(p) => addCommonStyles(p)};
`;

const StyledSubjectTooltip = styled(TooltipOverflowCondition)<CellMiddleStyleProps>`
  ${(p) => addCommonStyles(p)};
`;

/*
 * Component.
 */

export const CellMiddle: FC<CellMiddleProps> = memo((props) => {
  const {maxIndicators} = useMaximumNumberOfCellIndicators({
    highlight: props.subjectHighlight,
    numberOfTags: props.tags.length,
    isStarred: Boolean(props.isStarred),
    isOriginalConversation: Boolean(props.isOriginalConversation),
    isCompact: false,
  });
  return (
    <StyledFlexDiv>
      <StyledGridDiv>
        {renderCellSubject(props)}
        {props.isOriginalConversation && (
          <CellOriginalLinkedIcon
            isSelected={props.isSelected}
            numLinkedConversations={props.numLinkedConversations}
          />
        )}
        <CellTags maxTagsToShow={maxIndicators} tags={props.tags} isStarred={props.isStarred} />
      </StyledGridDiv>
      {props.conversation && props.ticketStatus && <CellTicketStatusPill conversation={props.conversation} />}
      <CellBadgeValue isSelected={props.isSelected} badgeValue={props.badgeValue} />
    </StyledFlexDiv>
  );
});

/*
 * Channel-specific logic.
 */

export function renderCellSubject(
  props: Omit<CellMiddleProps, 'badgeValue' | 'isStarred' | 'tags'> & {
    isCompact?: boolean;
    hasTooltip?: boolean;
  },
) {
  const {hasTooltip, messageType, subject, isSelected, subjectHighlight} = props;

  if (messageType === MessageTypesEnum.GOOGLE_PLAY) {
    return <CellRating rating={subject} isSelected={isSelected} />;
  }

  if (hasTooltip) {
    return (
      <StyledSubjectTooltip
        $isSelected={props.isSelected}
        $hasHighlight={isDefined(props.subjectHighlight)}
        $isCompact={Boolean(props.isCompact)}
        overrideCondition={subjectHighlight && subjectHighlight.text !== subject}
        checkChildrenOverflow
      >
        {renderCellSubjectContent({...props, isCompact: props.isCompact})}
      </StyledSubjectTooltip>
    );
  }

  return (
    <StyledSubjectDiv
      $isSelected={props.isSelected}
      $hasHighlight={isDefined(props.subjectHighlight)}
      $isCompact={Boolean(props.isCompact)}
    >
      {renderCellSubjectContent({...props, isCompact: props.isCompact})}
    </StyledSubjectDiv>
  );
}

export function renderCellSubjectContent(
  props: Omit<CellMiddleProps, 'badgeValue' | 'isStarred' | 'tags'> & {
    isCompact?: boolean;
  },
) {
  const {
    messageType,
    subject,
    subjectHighlight,
    recipient,
    externalChannelType,
    applicationChannelType,
    isSelected,
    isCompact,
    channelTypes,
  } = props;
  switch (messageType) {
    // Some conversations have a subject.
    case MessageTypesEnum.EMAIL:
    case MessageTypesEnum.INTERNAL:
      return renderTextSubject(
        subject,
        messageType,
        isSelected,
        subjectHighlight,
        undefined,
        undefined,
        isCompact,
      );

    // Custom messages may use externalChannelType or applicationChannelType for the name.
    case MessageTypesEnum.CUSTOM:
      return renderTextSubject(
        subject,
        messageType,
        isSelected,
        subjectHighlight,
        externalChannelType,
        applicationChannelType,
      );

    // Others have human-readable handles.
    case MessageTypesEnum.TWEET:
    case MessageTypesEnum.TWEET_DM:
    case MessageTypesEnum.CALL:
    case MessageTypesEnum.SMS:
      return maybeRenderHandleSubject(messageType, recipient);

    // Portal channels have subjects but use Front Chat.
    case MessageTypesEnum.FRONT_CHAT: {
      if (channelTypes.includes(ChannelTypesEnum.PORTAL)) {
        return renderTextSubject(
          subject,
          messageType,
          isSelected,
          subjectHighlight,
          undefined,
          undefined,
          isCompact,
          // We want to render "No subject" instead of falling back to the message type.
          renderPlaceholder(MessageTypesEnum.EMAIL),
        );
      }
      return renderPlaceholder(messageType);
    }

    // Otherwise, always show the placeholder.
    default:
      return renderPlaceholder(messageType);
  }
}

export function renderTextSubject(
  subject: string,
  messageType: MessageTypesEnum,
  isSelected?: boolean,
  highlight?: HighlightModel,
  externalChannelType?: ExternalChannelTypeModel,
  applicationChannelType?: ApplicationChannelTypeModel,
  isCompact?: boolean,
  renderedCustomPlaceholder?: ReactNode,
) {
  // If we have a highlight, use it.
  if (highlight) {
    return (
      <Highlight
        {...highlight}
        backgroundColor={PaletteColorsEnum.YELLOW}
        isParentSelected={isSelected}
        hideWhitespaceOnly={isCompact}
      />
    );
  }

  // If we have a subject, render that.
  const displaySubject = subject.trim();
  if (displaySubject) {
    return <MutationProofText>{displaySubject}</MutationProofText>;
  }

  // If we have a application channel type, render that instead of placeholder.
  if (applicationChannelType) {
    return applicationChannelType.name;
  }

  // If we have a external channel type, render that instead of placeholder.
  if (externalChannelType) {
    return externalChannelType.name;
  }

  // Otherwise, render a placeholder.
  return renderedCustomPlaceholder || renderPlaceholder(messageType);
}

function maybeRenderHandleSubject(messageType: MessageTypesEnum, recipient?: VisualRecipientModel) {
  // A recipient is needed at this point.
  if (!recipient) {
    return renderPlaceholder(messageType);
  }

  const name = buildContactDisplayName(recipient);
  const handle = buildContactDisplayHandle(recipient);

  // If the name and handle are different, render the handle as subject.
  if (name !== handle) {
    return handle;
  }

  // Otherwise, render the placeholder.
  return renderPlaceholder(messageType);
}

function renderPlaceholder(messageType: MessageTypesEnum) {
  return <em>{placeholderSubjectsByMessageType[messageType]}</em>;
}

type PlaceholderRenderers = {[T in MessageTypesEnum]: ReactNode};
// prettier-ignore
export const placeholderSubjectsByMessageType: PlaceholderRenderers = {
  [MessageTypesEnum.INTERNAL]: <Trans id="cell-placeholder-internal">No topic</Trans>,
  [MessageTypesEnum.EMAIL]: <Trans id="cell-placeholder-email">No subject</Trans>,
  [MessageTypesEnum.TWEET]: <Trans id="cell-placeholder-twitter">X</Trans>,
  [MessageTypesEnum.TWEET_DM]: <Trans id="cell-placeholder-twitterdm">X DM</Trans>,
  [MessageTypesEnum.CALL]: <Trans id="cell-placeholder-call">Phone call</Trans>,
  [MessageTypesEnum.SMS]: <Trans id="cell-placeholder-sms">SMS</Trans>,
  [MessageTypesEnum.GOOGLE_PLAY]: <Trans id="cell-placeholder-googleplay">Google Play Review</Trans>,
  [MessageTypesEnum.FACEBOOK]: <Trans id="cell-placeholder-facebook">Facebook</Trans>,
  [MessageTypesEnum.INTERCOM]: <Trans id="cell-placeholder-intercom">Intercom</Trans>,
  [MessageTypesEnum.SMOOCH]: <Trans id="cell-placeholder-smooch">Smooch</Trans>,
  [MessageTypesEnum.LAYER]: <Trans id="cell-placeholder-chat">Chat</Trans>,
  [MessageTypesEnum.CUSTOM]: <Trans id="cell-placeholder-custom">Custom</Trans>,
  [MessageTypesEnum.YALO_WHATSAPP]: <Trans id="cell-placeholder-whatsapp">WhatsApp</Trans>,
  [MessageTypesEnum.WHATSAPP]: <Trans id="cell-placeholder-whatsapp">WhatsApp</Trans>,
  [MessageTypesEnum.FRONT_CHAT]: <Trans id="cell-placeholder-frontchat">Chat</Trans>,
};
